import * as BCP47 from "bcp-47";
import emojiFlags from "emoji-flags";
import { Organization, User } from "./hooks/api-new";

export type TextLanguage =
  | "af"
  | "sq"
  | "am"
  | "ar"
  | "hy"
  | "as"
  | "az"
  | "bs"
  | "bg"
  | "ca"
  | "zh-Hant"
  | "yue"
  | "zh-Hans"
  | "hr"
  | "cs"
  | "da"
  | "nl"
  | "en"
  | "et"
  | "fil"
  | "fi"
  | "fr"
  | "fr-ca"
  | "de"
  | "el"
  | "gu"
  | "he"
  | "hi"
  | "hu"
  | "is"
  | "id"
  | "ga"
  | "it"
  | "ja"
  | "kn"
  | "kk"
  | "km"
  | "ko"
  | "lo"
  | "lv"
  | "lt"
  | "ms"
  | "ml"
  | "mt"
  | "mr"
  | "my"
  | "ne"
  | "nb"
  | "or"
  | "ps"
  | "fa"
  | "pl"
  | "pt"
  | "pt-pt"
  | "pa"
  | "ro"
  | "ru"
  | "sr-Cyrl"
  | "sk"
  | "sl"
  | "es"
  | "sw"
  | "sv"
  | "ta"
  | "te"
  | "th"
  | "tr"
  | "uk"
  | "ur"
  | "vi"
  | "cy";

export type SpeechLanguage =
  | "af-ZA"
  | "sq-AL"
  | "am-ET"
  | "ar-AE"
  | "ar-BH"
  | "ar-DZ"
  | "ar-EG"
  | "ar-IL"
  | "ar-IQ"
  | "ar-JO"
  | "ar-KW"
  | "ar-LB"
  | "ar-LY"
  | "ar-MA"
  | "ar-OM"
  | "ar-PS"
  | "ar-QA"
  | "ar-SA"
  | "ar-SY"
  | "ar-TN"
  | "ar-YE"
  | "hy-AM"
  | "as-IN"
  | "az-AZ"
  | "bs-BA"
  | "bg-BG"
  | "ca-ES"
  | "zh-TW"
  | "zh-HK"
  | "yue-CN"
  | "zh-CN"
  | "hr-HR"
  | "cs-CZ"
  | "da-DK"
  | "nl-BE"
  | "nl-NL"
  | "en-AU"
  | "en-CA"
  | "en-GB"
  | "en-GH"
  | "en-HK"
  | "en-IE"
  | "en-IN"
  | "en-KE"
  | "en-NG"
  | "en-NZ"
  | "en-PH"
  | "en-SG"
  | "en-TZ"
  | "en-US"
  | "en-ZA"
  | "et-EE"
  | "fil-PH"
  | "fi-FI"
  | "fr-BE"
  | "fr-CH"
  | "fr-FR"
  | "fr-CA"
  | "de-AT"
  | "de-CH"
  | "de-DE"
  | "el-GR"
  | "gu-IN"
  | "he-IL"
  | "hi-IN"
  | "hu-HU"
  | "is-IS"
  | "id-ID"
  | "ga-IE"
  | "it-IT"
  | "ja-JP"
  | "kn-IN"
  | "kk-KZ"
  | "km-KH"
  | "ko-KR"
  | "lo-LA"
  | "lv-LV"
  | "lt-LT"
  | "ms-MY"
  | "ml-IN"
  | "mt-MT"
  | "mr-IN"
  | "my-MM"
  | "ne-NP"
  | "nb-NO"
  | "or-IN"
  | "ps-AF"
  | "fa-IR"
  | "pl-PL"
  | "pt-BR"
  | "pt-PT"
  | "pa-IN"
  | "ro-RO"
  | "ru-RU"
  | "sr-RS"
  | "sk-SK"
  | "sl-SI"
  | "es-AR"
  | "es-BO"
  | "es-CL"
  | "es-CO"
  | "es-CR"
  | "es-CU"
  | "es-DO"
  | "es-EC"
  | "es-ES"
  | "es-GQ"
  | "es-GT"
  | "es-HN"
  | "es-MX"
  | "es-NI"
  | "es-PA"
  | "es-PE"
  | "es-PR"
  | "es-PY"
  | "es-SV"
  | "es-US"
  | "es-UY"
  | "es-VE"
  | "sw-KE"
  | "sw-TZ"
  | "sv-SE"
  | "ta-IN"
  | "te-IN"
  | "th-TH"
  | "tr-TR"
  | "uk-UA"
  | "ur-IN"
  | "vi-VN"
  | "cy-GB";

export const supportedLanguages: Record<TextLanguage, Set<SpeechLanguage>> = {
  // Afrikaans
  af: new Set(["af-ZA"]),
  // Albanian
  sq: new Set(["sq-AL"]),
  // Amharic
  am: new Set(["am-ET"]),
  // Arabic
  ar: new Set([
    "ar-AE",
    "ar-BH",
    "ar-DZ",
    "ar-EG",
    "ar-IL",
    "ar-IQ",
    "ar-JO",
    "ar-KW",
    "ar-LB",
    "ar-LY",
    "ar-MA",
    "ar-OM",
    "ar-PS",
    "ar-QA",
    "ar-SA",
    "ar-SY",
    "ar-TN",
    "ar-YE",
  ]),
  // Armenian
  hy: new Set(["hy-AM"]),
  // Assamese
  as: new Set(["as-IN"]),
  // Azerbaijani
  az: new Set(["az-AZ"]),
  // Bosnian
  bs: new Set(["bs-BA"]),
  // Bulgarian
  bg: new Set(["bg-BG"]),
  // Catalan
  ca: new Set(["ca-ES"]),
  // Chinese (Traditional)
  "zh-Hant": new Set(["zh-TW"]),
  yue: new Set(["zh-HK"]),
  // Chinese (Simplified)
  "zh-Hans": new Set(["yue-CN", "zh-CN"]),
  // Croatian
  hr: new Set(["hr-HR"]),
  // Czech
  cs: new Set(["cs-CZ"]),
  // Danish
  da: new Set(["da-DK"]),
  // Dutch
  nl: new Set(["nl-BE", "nl-NL"]),
  // English
  en: new Set([
    "en-AU",
    "en-CA",
    "en-GB",
    "en-GH",
    "en-HK",
    "en-IE",
    "en-IN",
    "en-KE",
    "en-NG",
    "en-NZ",
    "en-PH",
    "en-SG",
    "en-TZ",
    "en-US",
    "en-ZA",
  ]),
  // Estonian
  et: new Set(["et-EE"]),
  // Filipino/Tagalog
  fil: new Set(["fil-PH"]),
  // Finnish
  fi: new Set(["fi-FI"]),
  // French
  fr: new Set(["fr-BE", "fr-CH", "fr-FR"]),
  // French (Canada)
  "fr-ca": new Set(["fr-CA"]),
  // German
  de: new Set(["de-AT", "de-CH", "de-DE"]),
  // Greek
  el: new Set(["el-GR"]),
  // Gujarati
  gu: new Set(["gu-IN"]),
  // Hebrew
  he: new Set(["he-IL"]),
  // Hindi
  hi: new Set(["hi-IN"]),
  // Hungarian
  hu: new Set(["hu-HU"]),
  // Icelandic
  is: new Set(["is-IS"]),
  // Indonesian/Bahasa
  id: new Set(["id-ID"]),
  // Irish
  ga: new Set(["ga-IE"]),
  // Italian
  it: new Set(["it-IT"]),
  // Japanese
  ja: new Set(["ja-JP"]),
  // Kannada
  kn: new Set(["kn-IN"]),
  // Kazakh
  kk: new Set(["kk-KZ"]),
  // Khmer
  km: new Set(["km-KH"]),
  // Korean
  ko: new Set(["ko-KR"]),
  // Lao
  lo: new Set(["lo-LA"]),
  // Latvian
  lv: new Set(["lv-LV"]),
  // Lithuanian
  lt: new Set(["lt-LT"]),
  // Malay
  ms: new Set(["ms-MY"]),
  // Malayalam
  ml: new Set(["ml-IN"]),
  // Maltese
  mt: new Set(["mt-MT"]),
  // Marathi
  mr: new Set(["mr-IN"]),
  // Myanmar
  my: new Set(["my-MM"]),
  // Nepali
  ne: new Set(["ne-NP"]),
  // Norwegian
  nb: new Set(["nb-NO"]),
  // Odia
  or: new Set(["or-IN"]),
  // Pashto
  ps: new Set(["ps-AF"]),
  // Persian
  fa: new Set(["fa-IR"]),
  // Polish
  pl: new Set(["pl-PL"]),
  // Portuguese (Brazil)
  pt: new Set(["pt-BR"]),
  // Portuguese (Portugal)
  "pt-pt": new Set(["pt-PT"]),
  // Punjabi
  pa: new Set(["pa-IN"]),
  // Romanian
  ro: new Set(["ro-RO"]),
  // Russian
  ru: new Set(["ru-RU"]),
  // Serbian (Cyrillic)
  "sr-Cyrl": new Set(["sr-RS"]),
  // Slovak
  sk: new Set(["sk-SK"]),
  // Slovenian
  sl: new Set(["sl-SI"]),
  // Spanish
  es: new Set([
    "es-AR",
    "es-BO",
    "es-CL",
    "es-CO",
    "es-CR",
    "es-CU",
    "es-DO",
    "es-EC",
    "es-ES",
    "es-GQ",
    "es-GT",
    "es-HN",
    "es-MX",
    "es-NI",
    "es-PA",
    "es-PE",
    "es-PR",
    "es-PY",
    "es-SV",
    "es-US",
    "es-UY",
    "es-VE",
  ]),
  // Swahili
  sw: new Set(["sw-KE", "sw-TZ"]),
  // Swedish
  sv: new Set(["sv-SE"]),
  // Tamil
  ta: new Set(["ta-IN"]),
  // Telugu
  te: new Set(["te-IN"]),
  // Thai
  th: new Set(["th-TH"]),
  // Turkish
  tr: new Set(["tr-TR"]),
  // Ukrainian
  uk: new Set(["uk-UA"]),
  // Urdu
  ur: new Set(["ur-IN"]),
  // Vietnamese
  vi: new Set(["vi-VN"]),
  // Welsh
  cy: new Set(["cy-GB"]),
} as const;

export const textLanguageNames: Record<TextLanguage, string> = {
  af: "Afrikaans",
  sq: "Shqip",
  am: "አማርኛ",
  ar: "العربية",
  hy: "Հայերեն",
  as: "অসমীয়া",
  az: "Azərbaycanca",
  bs: "Bosanski",
  bg: "Български",
  ca: "Català",
  "zh-Hant": "繁體中文",
  yue: "粵語",
  "zh-Hans": "简体中文",
  hr: "Hrvatski",
  cs: "Čeština",
  da: "Dansk",
  nl: "Nederlands",
  en: "English",
  et: "Eesti",
  fil: "Filipino",
  fi: "Suomi",
  fr: "Français",
  "fr-ca": "Français (Canada)",
  de: "Deutsch",
  el: "Ελληνικά",
  gu: "ગુજરાતી",
  he: "עברית",
  hi: "हिन्दी",
  hu: "Magyar",
  is: "Íslenska",
  id: "Bahasa Indonesia",
  ga: "Gaeilge",
  it: "Italiano",
  ja: "日本語",
  kn: "ಕನ್ನಡ",
  kk: "Қазақша",
  km: "ខ្មែរ",
  ko: "한국어",
  lo: "ລາວ",
  lv: "Latviešu",
  lt: "Lietuvių",
  ms: "Bahasa Melayu",
  ml: "മലയാളം",
  mt: "Malti",
  mr: "मराठी",
  my: "မြန်မာဘာသာ",
  ne: "नेपाली",
  nb: "Norsk Bokmål",
  or: "ଓଡ଼ିଆ",
  ps: "پښتو",
  fa: "فارسی",
  pl: "Polski",
  pt: "Português (Brasil)",
  "pt-pt": "Português (Portugal)",
  pa: "ਪੰਜਾਬੀ",
  ro: "Română",
  ru: "Русский",
  "sr-Cyrl": "Српски (Ћирилица)",
  sk: "Slovenčina",
  sl: "Slovenščina",
  es: "Español",
  sw: "Kiswahili",
  sv: "Svenska",
  ta: "தமிழ்",
  te: "తెలుగు",
  th: "ไทย",
  tr: "Türkçe",
  uk: "Українська",
  ur: "اردو",
  vi: "Tiếng Việt",
  cy: "Cymraeg",
};

export const languageNames: Record<SpeechLanguage, string> = {
  "af-ZA": "Afrikaans (Suid-Afrika)",
  "sq-AL": "Shqip (Shqipëri)",
  "am-ET": "አማርኛ (ኢትዮጵያ)",
  "ar-AE": "العربية (الإمارات العربية المتحدة)",
  "ar-BH": "العربية (البحرين)",
  "ar-DZ": "العربية (الجزائر)",
  "ar-EG": "العربية (مصر)",
  "ar-IL": "العربية (إسرائيل)",
  "ar-IQ": "العربية (العراق)",
  "ar-JO": "العربية (الأردن)",
  "ar-KW": "العربية (الكويت)",
  "ar-LB": "العربية (لبنان)",
  "ar-LY": "العربية (ليبيا)",
  "ar-MA": "العربية (المغرب)",
  "ar-OM": "العربية (عمان)",
  "ar-PS": "العربية (فلسطين)",
  "ar-QA": "العربية (قطر)",
  "ar-SA": "العربية (السعودية)",
  "ar-SY": "العربية (سوريا)",
  "ar-TN": "العربية (تونس)",
  "ar-YE": "العربية (اليمن)",
  "hy-AM": "Հայերեն (Հայաստան)",
  "as-IN": "অসমীয়া (ভাৰত)",
  "az-AZ": "Azərbaycanca (Azərbaycan)",
  "bs-BA": "Bosanski (Bosna i Hercegovina)",
  "bg-BG": "Български (България)",
  "ca-ES": "Català (Espanya)",
  "zh-TW": "繁體中文 (台灣)",
  "zh-HK": "粵語 (香港)",
  "yue-CN": "粵語 (中國)",
  "zh-CN": "简体中文 (中国)",
  "hr-HR": "Hrvatski (Hrvatska)",
  "cs-CZ": "Čeština (Česko)",
  "da-DK": "Dansk (Danmark)",
  "nl-BE": "Nederlands (België)",
  "nl-NL": "Nederlands (Nederland)",
  "en-AU": "English (Australia)",
  "en-CA": "English (Canada)",
  "en-GB": "English (United Kingdom)",
  "en-GH": "English (Ghana)",
  "en-HK": "English (Hong Kong)",
  "en-IE": "English (Ireland)",
  "en-IN": "English (India)",
  "en-KE": "English (Kenya)",
  "en-NG": "English (Nigeria)",
  "en-NZ": "English (New Zealand)",
  "en-PH": "English (Philippines)",
  "en-SG": "English (Singapore)",
  "en-TZ": "English (Tanzania)",
  "en-US": "English (United States)",
  "en-ZA": "English (South Africa)",
  "et-EE": "Eesti (Eesti)",
  "fil-PH": "Filipino (Pilipinas)",
  "fi-FI": "Suomi (Suomi)",
  "fr-BE": "Français (Belgique)",
  "fr-CH": "Français (Suisse)",
  "fr-FR": "Français (France)",
  "fr-CA": "Français (Canada)",
  "de-AT": "Deutsch (Österreich)",
  "de-CH": "Deutsch (Schweiz)",
  "de-DE": "Deutsch (Deutschland)",
  "el-GR": "Ελληνικά (Ελλάδα)",
  "gu-IN": "ગુજરાતી (ભારત)",
  "he-IL": "עברית (ישראל)",
  "hi-IN": "हिन्दी (भारत)",
  "hu-HU": "Magyar (Magyarország)",
  "is-IS": "Íslenska (Ísland)",
  "id-ID": "Bahasa Indonesia (Indonesia)",
  "ga-IE": "Gaeilge (Éire)",
  "it-IT": "Italiano (Italia)",
  "ja-JP": "日本語 (日本)",
  "kn-IN": "ಕನ್ನಡ (ಭಾರತ)",
  "kk-KZ": "Қазақ тілі (Қазақстан)",
  "km-KH": "ភាសាខ្មែរ (កម្ពុជា)",
  "ko-KR": "한국어 (대한민국)",
  "lo-LA": "ລາວ (ປະເທດລາວ)",
  "lv-LV": "Latviešu (Latvija)",
  "lt-LT": "Lietuvių (Lietuva)",
  "ms-MY": "Bahasa Melayu (Malaysia)",
  "ml-IN": "മലയാളം (ഇന്ത്യ)",
  "mt-MT": "Malti (Malta)",
  "mr-IN": "मराठी (भारत)",
  "my-MM": "မြန်မာဘာသာ (မြန်မာ)",
  "ne-NP": "नेपाली (नेपाल)",
  "nb-NO": "Norsk Bokmål (Norge)",
  "or-IN": "ଓଡ଼ିଆ (ଭାରତ)",
  "ps-AF": "پښتو (افغانستان)",
  "fa-IR": "فارسی (ایران)",
  "pl-PL": "Polski (Polska)",
  "pt-BR": "Português (Brasil)",
  "pt-PT": "Português (Portugal)",
  "pa-IN": "ਪੰਜਾਬੀ (ਭਾਰਤ)",
  "ro-RO": "Română (România)",
  "ru-RU": "Русский (Россия)",
  "sr-RS": "Српски (Србија)",
  "sk-SK": "Slovenčina (Slovensko)",
  "sl-SI": "Slovenščina (Slovenija)",
  "es-AR": "Español (Argentina)",
  "es-BO": "Español (Bolivia)",
  "es-CL": "Español (Chile)",
  "es-CO": "Español (Colombia)",
  "es-CR": "Español (Costa Rica)",
  "es-CU": "Español (Cuba)",
  "es-DO": "Español (República Dominicana)",
  "es-EC": "Español (Ecuador)",
  "es-ES": "Español (España)",
  "es-GQ": "Español (Guinea Ecuatorial)",
  "es-GT": "Español (Guatemala)",
  "es-HN": "Español (Honduras)",
  "es-MX": "Español (México)",
  "es-NI": "Español (Nicaragua)",
  "es-PA": "Español (Panamá)",
  "es-PE": "Español (Perú)",
  "es-PR": "Español (Puerto Rico)",
  "es-PY": "Español (Paraguay)",
  "es-SV": "Español (El Salvador)",
  "es-US": "Español (Estados Unidos)",
  "es-UY": "Español (Uruguay)",
  "es-VE": "Español (Venezuela)",
  "sw-KE": "Kiswahili (Kenya)",
  "sw-TZ": "Kiswahili (Tanzania)",
  "sv-SE": "Svenska (Sverige)",
  "ta-IN": "தமிழ் (இந்தியா)",
  "te-IN": "తెలుగు (భారతదేశం)",
  "th-TH": "ไทย (ประเทศไทย)",
  "tr-TR": "Türkçe (Türkiye)",
  "uk-UA": "Українська (Україна)",
  "ur-IN": "اردو (بھارت)",
  "vi-VN": "Tiếng Việt (Việt Nam)",
  "cy-GB": "Cymraeg (Y Deyrnas Unedig)",
} as const;

export const languageSearchAliases: Record<string, string[]> = {
  "af-ZA": ["afrikaans"],
  "sq-AL": ["albanian"],
  "am-ET": ["amharic"],
  "ar-AE": ["arabic"],
  "ar-BH": ["arabic"],
  "ar-DZ": ["arabic"],
  "ar-EG": ["arabic"],
  "ar-IL": ["arabic"],
  "ar-IQ": ["arabic"],
  "ar-JO": ["arabic"],
  "ar-KW": ["arabic"],
  "ar-LB": ["arabic"],
  "ar-LY": ["arabic"],
  "ar-MA": ["arabic"],
  "ar-OM": ["arabic"],
  "ar-PS": ["arabic"],
  "ar-QA": ["arabic"],
  "ar-SA": ["arabic"],
  "ar-SY": ["arabic"],
  "ar-TN": ["arabic"],
  "ar-YE": ["arabic"],
  "hy-AM": ["armenian"],
  "as-IN": ["assamese"],
  "az-AZ": ["azerbaijani"],
  "bs-BA": ["bosnian"],
  "bg-BG": ["bulgarian"],
  "ca-ES": ["catalan"],
  "zh-TW": ["traditional chinese"],
  "zh-HK": ["cantonese"],
  "yue-CN": ["cantonese"],
  "zh-CN": ["simplified chinese"],
  "hr-HR": ["croatian"],
  "cs-CZ": ["czech"],
  "da-DK": ["danish"],
  "nl-BE": ["dutch"],
  "nl-NL": ["dutch"],
  "en-AU": ["english"],
  "en-CA": ["english"],
  "en-GB": ["english"],
  "en-GH": ["english"],
  "en-HK": ["english"],
  "en-IE": ["english"],
  "en-IN": ["english"],
  "en-KE": ["english"],
  "en-NG": ["english"],
  "en-NZ": ["english"],
  "en-PH": ["english"],
  "en-SG": ["english"],
  "en-TZ": ["english"],
  "en-US": ["english"],
  "en-ZA": ["english"],
  "et-EE": ["estonian"],
  "fil-PH": ["filipino", "tagalog"],
  "fi-FI": ["finnish"],
  "fr-BE": ["french"],
  "fr-CH": ["french"],
  "fr-FR": ["french"],
  "fr-CA": ["french"],
  "de-AT": ["german"],
  "de-CH": ["german"],
  "de-DE": ["german"],
  "el-GR": ["greek"],
  "gu-IN": ["gujarati"],
  "he-IL": ["hebrew"],
  "hi-IN": ["hindi"],
  "hu-HU": ["hungarian"],
  "is-IS": ["icelandic"],
  "id-ID": ["bahasa"],
  "ga-IE": ["irish"],
  "it-IT": ["italian"],
  "ja-JP": ["japanese"],
  "kn-IN": ["kannada"],
  "kk-KZ": ["kazhak"],
  "km-KH": ["khmer", "cambodian"],
  "ko-KR": ["korean"],
  "lo-LA": ["lao"],
  "lv-LV": ["latvian"],
  "lt-LT": ["lithuanian"],
  "ms-MY": ["bahasa"],
  "ml-IN": ["malayalam"],
  "mt-MT": ["maltese"],
  "mr-IN": ["marathi"],
  "my-MM": ["burmese"],
  "ne-NP": ["nepali"],
  "nb-NO": ["norsk"],
  "or-IN": ["odia"],
  "ps-AF": ["pashto"],
  "fa-IR": ["persian"],
  "pl-PL": ["polish"],
  "pt-BR": ["brazilian portuguese"],
  "pt-PT": ["portuguese"],
  "pa-IN": ["punjabi"],
  "ro-RO": ["romanian"],
  "ru-RU": ["russian"],
  "sr-RS": ["serbian"],
  "sk-SK": ["slovak"],
  "sl-SI": ["slovene"],
  "es-AR": ["spanish"],
  "es-BO": ["spanish"],
  "es-CL": ["spanish"],
  "es-CO": ["spanish"],
  "es-CR": ["spanish"],
  "es-CU": ["spanish"],
  "es-DO": ["spanish"],
  "es-EC": ["spanish"],
  "es-ES": ["spanish"],
  "es-GQ": ["spanish"],
  "es-GT": ["spanish"],
  "es-HN": ["spanish"],
  "es-MX": ["spanish"],
  "es-NI": ["spanish"],
  "es-PA": ["spanish"],
  "es-PE": ["spanish"],
  "es-PR": ["spanish"],
  "es-PY": ["spanish"],
  "es-SV": ["spanish"],
  "es-US": ["spanish"],
  "es-UY": ["spanish"],
  "es-VE": ["spanish"],
  "sw-KE": ["swahili"],
  "sw-TZ": ["swahili"],
  "sv-SE": ["swedish"],
  "ta-IN": ["tamil"],
  "te-IN": ["telugu"],
  "th-TH": ["thai"],
  "tr-TR": ["turkish"],
  "uk-UA": ["ukrainian"],
  "ur-IN": ["urdu"],
  "vi-VN": ["vietnamese"],
  "cy-GB": ["welsh"],
};

export const topLanguages = [
  "autodetect",
  "en-US",
  "zh-CN",
  "es-MX",
  "ja-JP",
  "ko-KR",
  "es-CO",
  "ru-RU",
  "pt-BR",
  "fr-FR",
  "sw-KE",
  "id-ID",
  "zh-HK",
];

export function getLanguageName(speechLanguage: string): string | undefined {
  if (speechLanguage === "autodetect") {
    return "Auto-Detect";
  }
  return languageNames[speechLanguage as SpeechLanguage];
}

export const getLanguageFlag = (speechLanguage: string): string => {
  if (speechLanguage === "autodetect") {
    return "✨";
  }
  // Parse language code to get country
  const schema = BCP47.parse(speechLanguage);

  if (schema.region) {
    return emojiFlags.countryCode(schema.region).emoji;
  }

  // Default return space string
  return " ";
};

export function getTextLanguage(speechLanguage: string): string | undefined {
  for (const textLanguage in supportedLanguages) {
    if (
      supportedLanguages[textLanguage as TextLanguage].has(
        speechLanguage as SpeechLanguage
      )
    ) {
      return textLanguage;
    }
  }
  return undefined;
}

export enum LocalStorageKeys {
  EXPECT_LOGIN = "EXPECT_LOGIN",
  LANGFINITY_TOKEN = "LANGFINITY_TOKEN",
  SITE_LANGUAGE = "SITE_LANGUAGE",
  USER_METADATA_NAME = "USER_METADATA_NAME",
  USER_METADATA_LANGUAGE = "USER_METADATA_LANGUAGE",
  CAPTIONS = "CAPTIONS",
  CAPTIONS_LAST_ACCESS = "CAPTIONS_LAST_ACCESS",
  MEETING_ID = "MEETING_ID",
  LAYOUT_SETTINGS = "LAYOUT_SETTINGS",
  VOICE_AVATAR = "VOICE_AVATAR",
  VIDEO_BACKGROUND = "VIDEO_BACKGROUND",
  TOP_LANGUAGES = "TOP_LANGUAGES",
}

export type FirebaseAuthToken = {
  name: string;
  email: string;
  email_verified: boolean;
  exp: number;
  iat: number;
  iss: string;
  user_id: string;
  picture: string;
};

export const participantTypes = ["speaker", "interpreter", "viewer"] as const;
export type Gender = "male" | "female";

export const topVoices: Record<Gender, Record<string, string[]>> = {
  male: {
    "en-US": ["voice1", "voice2", "voice3"],
    "zh-CN": ["voice1", "voice2", "voice3"],
    "es-MX": ["voice1", "voice2", "voice3"],
    "es-CO": ["voice1"],
    "ru-RU": ["voice1"],
    "ja-JP": ["voice1", "voice2", "voice3"],
    "fr-FR": ["voice1", "voice2", "voice3"],
    "sw-KE": ["voice1"],
    "id-ID": ["voice1"],
    "ko-KR": ["voice1", "voice2", "voice3"],
  },
  female: {
    "en-US": ["voice1", "voice2", "voice3"],
    "zh-CN": ["voice1", "voice2", "voice3"],
    "es-MX": ["voice1", "voice2", "voice3"],
    "es-CO": ["voice1"],
    "ru-RU": ["voice1", "voice2"],
    "ja-JP": ["voice1", "voice2", "voice3"],
    "fr-FR": ["voice1", "voice2", "voice3"],
    "sw-KE": ["voice1"],
    "id-ID": ["voice1"],
    "ko-KR": ["voice1", "voice2", "voice3"],
  },
};

export const s3Dir = `https://byrdhouse-public-prod.s3.us-west-1.amazonaws.com/`;

export interface Voice {
  id: string;
  rank: number;
  previewUrl: string;
  gender: Gender;
}

export type UserState = {
  auth?: User;
  metadata: {
    name?: string;
    botParticipantId?: string;
    participantType?: (typeof participantTypes)[number];
    selectedLanguage: string;
    detectedLanguage?: string;
    // TODO: Switch to boolean
    voice?: "ai";
    aiVoiceGender: string;
    aiVoice: string;
    features?: Pick<
      Organization,
      | "autodetect_enabled"
      | "glossary_enabled"
      | "max_room_participants_allowed"
      | "recording_enabled"
      | "voice_translation_enabled"
      | "voice_avatar_enabled"
      | "human_interpreter_enabled"
    >;
    lastActiveAt?: number;
    timezone?: string;
  };
  livekitId: string;
};

export const initialUserState: UserState = {
  metadata: {
    selectedLanguage:
      localStorage.getItem(LocalStorageKeys.USER_METADATA_LANGUAGE) ||
      (Object.values(supportedLanguages)
        .flatMap((l) => Array.from(l))
        .includes(navigator.language as SpeechLanguage)
        ? navigator.language
        : "en-US"),
    aiVoiceGender: "female",
    aiVoice: "anyVoice",
    timezone:
      Intl.DateTimeFormat().resolvedOptions().timeZone ||
      Intl.DateTimeFormat().resolvedOptions().timeZoneName,
  },
  livekitId: crypto.randomUUID().toString(),
};

export type LayoutState = {
  settings: {
    textTranslationEnabled: boolean;
    textSize: number;
    textColor: string;
    textBackgroundColor: string;
    textSpeed: number;
  };
};

export const initialLayoutState: LayoutState = {
  settings: localStorage.getItem(LocalStorageKeys.LAYOUT_SETTINGS)
    ? {
        ...JSON.parse(localStorage.getItem(LocalStorageKeys.LAYOUT_SETTINGS)!),
        textTranslationEnabled: true,
      }
    : {
        textSize: 14,
        textColor: "#101010",
        textBackgroundColor: "#fff",
        textSpeed: 0,
        textTranslationEnabled: true,
      },
};

export const videoBackgroundOptions = [undefined, "blur"] as const;

export type EventViewerState = {
  joined: boolean;
  layout: {
    participantsGridHeightInPercent: number;
    overlayOpen: boolean;
  };
  media: {
    videoBackground: (typeof videoBackgroundOptions)[number];
  };
};

export const initialEventViewerState: EventViewerState = {
  joined: false,
  layout: {
    participantsGridHeightInPercent: 60,
    overlayOpen: false,
  },
  media: { videoBackground: undefined },
};

export type MeetingState = {
  joined: boolean;
  layout: {
    participantsGridHeightInPercent: number;
    rightSidePanelWidthInPercent: number;
    panels: {
      participants: boolean;
      captions: boolean;
      side?: "chat" | "participants";
    };
  };
  overlayOpen: boolean;
  media: {
    videoBackground: (typeof videoBackgroundOptions)[number];
    applyingBackground: boolean;
  };
};

export const initialMeetingState: MeetingState = {
  joined: false,
  layout: {
    participantsGridHeightInPercent: 70,
    rightSidePanelWidthInPercent: 20,
    panels: {
      participants: true,
      captions: true,
    },
  },
  overlayOpen: false,
  media: {
    videoBackground:
      (localStorage.getItem(LocalStorageKeys.VIDEO_BACKGROUND) as
        | (typeof videoBackgroundOptions)[number]
        | null) ?? undefined,
    applyingBackground: false,
  },
};

export type LocalizationState = {
  siteLanguage: string;
};

export const initialLocalizationState: LocalizationState = {
  siteLanguage:
    localStorage.getItem(LocalStorageKeys.SITE_LANGUAGE) ||
    getTextLanguage(navigator.language) ||
    navigator.language,
};

export type RedirectAfterLoginState = {
  pathname?: string;
};

export const initialRedirectAfterLoginState: RedirectAfterLoginState = {};

export type Caption = {
  id: string;
  translations: { [language: string]: string };
  complete: boolean;
  name: string;
  identity: string;
  language: string | undefined;
  edited?: boolean;
};

export type CaptionsState = {
  [id: string]: Caption;
};

export const initialCaptionsState: CaptionsState = {};

export function getUrl(path: string) {
  const baseUrl = window.location.origin;
  if (path.length && path.charAt(0) !== "/") {
    path = "/" + path;
  }
  const url = new URL(path, baseUrl);
  return url.toString();
}

export type TranslatedText = {
  [languageCode: string]: string; // e.g. { en: "Hello", es: "Hola" }
};

export type ReceivedMessage = {
  messageId: string;
  text: TranslatedText;
  name: string;
  identity: string;
  timestamp: string; // ISO String
};

export type DisplayMessage = {
  pending?: boolean;
  messageId: string;
  text: TranslatedText;
  name: string;
  identity: string;
  timestamp: string; // ISO String
};

export interface EmojiData {
  id: number;
  emoji: string;
  name: string;
  startX: number;
  duration: number;
}

export interface FloatingEmojiProps extends EmojiData {}
